import { useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Button, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { CustomLoading } from 'components/CustomLoading';
import toastr from 'toastr';
import { Pencil, Check, X } from 'lucide-react';

const JgDirectorsReport = () => {
  const [loading, setLoading] = useState(true);
  const [provinces, setProvinces] = useState([]);
  const [distros, setDistros] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ rmd: null, bdd1: null, bdd2: null });
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/JgDirectors');
      setProvinces(response.data.provinces);
      setDistros(response.data.distros);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toastr.error('Failed to load RMD/BDD assignments');
      setLoading(false);
    }
  };

  const distroOptions = useMemo(
    () => distros.map((distro) => ({
      value: distro.id,
      label: `${distro.healthClaimAccountId} - ${distro.name}${distro.isActive ? '' : ' (inactive)'}`,
    })),
    [distros]
  );

  const optionFor = (id) => distroOptions.find((opt) => opt.value === id) ?? null;

  const startEdit = (row) => {
    setEditing(row.province);
    setDraft({ rmd: row.rmd, bdd1: row.bdd1, bdd2: row.bdd2 });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({ rmd: null, bdd1: null, bdd2: null });
  };

  const requestSave = (row) => {
    if (draft.bdd1 && draft.bdd1 === draft.bdd2) {
      toastr.error('BDD1 and BDD2 must be different distros');
      return;
    }
    setPending(row);
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`/api/JgDirectors/${pending.province}`, draft);
      toastr.success(`Updated ${pending.provinceName} — ${response.data.advisorsUpdated} advisor(s) reassigned`);
      setPending(null);
      cancelEdit();
      await fetchData();
    } catch (e) {
      const err = e as AxiosError;
      toastr.error(err.response?.data ?? 'Failed to save RMD/BDD assignment');
    } finally {
      setSaving(false);
    }
  };

  const renderCell = (row, field, label) => {
    if (editing !== row.province) {
      return row[`${field}Name`] ?? 'Not set';
    }
    return (
      <Select
        options={distroOptions}
        value={optionFor(draft[field])}
        onChange={(selected) => setDraft({ ...draft, [field]: selected ? selected.value : null })}
        isSearchable
        isClearable
        placeholder={`Choose ${label}`}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };

  return (
    <>
      {loading ? (
        <CustomLoading />
      ) : (
        <>
          <Modal show={pending != null} onHide={() => setPending(null)} className="Account-Details-Modal">
            <Modal.Header closeButton>
              <Modal.Title className="advisor-modal-title">Confirm RMD/BDD change</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                This will overwrite the RMD, BDD1 and BDD2 on{' '}
                <strong>{pending?.advisorCount ?? 0}</strong> active JG advisor(s) in{' '}
                <strong>{pending?.provinceName}</strong>, and retag the affected distro accounts.
              </p>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  <tr>
                    <td className="table-text-content">RMD</td>
                    <td className="table-text-content">{optionFor(draft.rmd)?.label ?? 'Not set'}</td>
                  </tr>
                  <tr>
                    <td className="table-text-content">BDD1</td>
                    <td className="table-text-content">{optionFor(draft.bdd1)?.label ?? 'Not set'}</td>
                  </tr>
                  <tr>
                    <td className="table-text-content">BDD2</td>
                    <td className="table-text-content">{optionFor(draft.bdd2)?.label ?? 'Not set'}</td>
                  </tr>
                </tbody>
              </Table>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={confirmSave} disabled={saving}>
                {saving ? 'Saving...' : 'Apply'}
              </Button>
              <Button variant="secondary" onClick={() => setPending(null)} disabled={saving}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
          <div className="dashboard-table-container">
            <Table hover className="table table-hover search-results-table">
              <thead>
                <tr>
                  <th className="table-text-content">Province</th>
                  <th className="table-text-content">RMD</th>
                  <th className="table-text-content">BDD1</th>
                  <th className="table-text-content">BDD2</th>
                  <th className="table-text-content">JG Advisors</th>
                  <th className="table-text-content">Last Updated</th>
                  <th className="table-text-content">Actions</th>
                </tr>
              </thead>
              <tbody>
                {provinces.map((row) => (
                  <tr key={row.province} className="table-body-item">
                    <td className="table-text-content">{row.provinceName}</td>
                    <td className="table-text-content">{renderCell(row, 'rmd', 'RMD')}</td>
                    <td className="table-text-content">{renderCell(row, 'bdd1', 'BDD1')}</td>
                    <td className="table-text-content">{renderCell(row, 'bdd2', 'BDD2')}</td>
                    <td className="table-text-content">{row.advisorCount}</td>
                    <td className="table-text-content">
                      {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex align-items-center gap-1 flex-nowrap">
                        {editing === row.province ? (
                          <>
                            <Button variant="primary" size="sm" className="search-action-btn" onClick={() => requestSave(row)}>
                              <Check size={14} /> Save
                            </Button>
                            <Button variant="outline-primary" size="sm" className="search-action-btn" onClick={cancelEdit}>
                              <X size={14} /> Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            className="search-action-btn"
                            disabled={editing != null}
                            onClick={() => startEdit(row)}
                          >
                            <Pencil size={14} /> Edit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </>
  );
};

export default JgDirectorsReport;
